import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllCategories,
  fetchCategories,
  fetchLandingPageCategories,
  fetchPartsByCategories,
  fetchPartsBySingleCategory,
  filterCategories,
} from "./categoriesAction";

const initialState = {
  allCategories: [],
  categories: [],
  partsByCategory: {},
  landingPageCategories: [],
  partsBySingleCategory: [],
  filteredParts: [],
  filteredCategory: [],

  categoriesLoading: false,
  categoriesLoadingMore: false,

  singleCategoryLoading: false,
  singleCategoryLoadingMore: false,

  filtering: false,

  categoriesLastVisible: null,
  categoriesHasMore: false,

  singleCategoryLastVisible: null,
  singleCategoryHasMore: false,

  partsMapping: false,
};

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch categories (with optional load more)
      .addCase(fetchCategories.pending, (state, action) => {
        const isLoadMore = action.meta.arg?.isLoadMore;
        if (isLoadMore) {
          state.categoriesLoadingMore = true;
        } else {
          state.categoriesLoading = true;
        }
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        const { categories, lastVisible, hasMore, isLoadMore } = action.payload;

        if (isLoadMore) {
          state.categories = [...state.categories, ...categories];
        } else {
          state.categories = categories;
        }

        state.categoriesLastVisible = lastVisible;
        state.categoriesHasMore = hasMore;

        state.categoriesLoadingMore = false;
        state.categoriesLoading = false;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.categoriesLoading = false;
        state.categoriesLoadingMore = false;
      })

      // Fetch parts for multiple categories (listing page)
      .addCase(fetchPartsByCategories.pending, (state, action) => {
        const isLoadMore = action.meta.arg?.isLoadMore;
        if (!isLoadMore) {
          state.partsByCategory = {};
        }
        if (isLoadMore) {
          state.categoriesLoadingMore = true;
        } else {
          state.categoriesLoading = true;
        }
        state.partsMapping = true;
      })
      .addCase(fetchPartsByCategories.fulfilled, (state, action) => {
        state.partsByCategory = action.payload;
        state.categoriesLoading = false;
        state.categoriesLoadingMore = false;
        state.partsMapping = false;
      })
      .addCase(fetchPartsByCategories.rejected, (state) => {
        state.categoriesLoading = false;
        state.categoriesLoadingMore = false;
        state.partsMapping = false;
      })

      // Fetch parts for single category
      .addCase(fetchPartsBySingleCategory.pending, (state, action) => {
        const isLoadMore = action.meta.arg?.lastVisible !== null;
        if (isLoadMore) {
          state.singleCategoryLoadingMore = true;
        } else {
          state.singleCategoryLoading = true;
        }
      })
      .addCase(fetchPartsBySingleCategory.fulfilled, (state, action) => {
        const { parts, lastVisible, hasMore, isLoadMore } = action.payload;

        if (isLoadMore) {
          state.partsBySingleCategory = [
            ...state.partsBySingleCategory,
            ...parts,
          ];
        } else {
          state.partsBySingleCategory = parts;
        }

        state.singleCategoryLastVisible = lastVisible;
        state.singleCategoryHasMore = hasMore;

        state.singleCategoryLoadingMore = false;
        state.singleCategoryLoading = false;
      })
      .addCase(fetchPartsBySingleCategory.rejected, (state) => {
        state.singleCategoryLoading = false;
        state.singleCategoryLoadingMore = false;
      })

      // Fetch all the categories without limit for filter
      .addCase(fetchAllCategories.pending, (state) => {
        state.singleCategoryLoading = true;
      })
      .addCase(fetchAllCategories.fulfilled, (state, action) => {
        state.allCategories = action.payload;
        state.singleCategoryLoading = false;
      })
      .addCase(fetchAllCategories.rejected, (state) => {
        state.singleCategoryLoading = false;
      })

      // Filter the categories
      .addCase(filterCategories.pending, (state) => {
        state.filtering = true;
      })
      .addCase(filterCategories.fulfilled, (state, action) => {
        state.filteredParts = action.payload?.parts;
        state.filteredCategory = action.payload?.category;
        state.filtering = false;
      })
      .addCase(filterCategories.rejected, (state) => {
        state.filtering = false;
      })

      // Fetch categories for landing page
      .addCase(fetchLandingPageCategories.pending, (state) => {
        state.listingsLoading = true;
      })
      .addCase(fetchLandingPageCategories.fulfilled, (state, action) => {
        state.listingsLoading = false;
        state.landingPageCategories = action.payload;
      })
      .addCase(fetchLandingPageCategories.rejected, (state) => {
        state.listingsLoading = false;
      });
  },
});

export const { resetCategoriesState } = categoriesSlice.actions;
export default categoriesSlice.reducer;
